import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { Calendar, Clock } from "lucide-react";
import { formatDate, formatTime } from "../../../../../../../utils/timeUtils";
import { NoteHeaderProps } from "../../types/VoiceNotes";

type NoteTypeColors = {
  backgroundColor: string;
  textColor: string;
};

const CUSTOM_NOTE_TYPE_PALETTE: NoteTypeColors[] = [
  { backgroundColor: "#F1EFF8", textColor: "#5B4F82" },
  { backgroundColor: "#E9F5F3", textColor: "#3D6B64" },
  { backgroundColor: "#FCEFF1", textColor: "#A44C63" },
  { backgroundColor: "#F8F3E8", textColor: "#8A6A2D" },
  { backgroundColor: "#ECF6F2", textColor: "#2F6F5A" },
  { backgroundColor: "#EAF4F8", textColor: "#2D647A" },
  { backgroundColor: "#EEF1F4", textColor: "#506070" },
  { backgroundColor: "#F5EFF4", textColor: "#7A5473" },
];

const BUILTIN_NOTE_TYPE_COLORS: Record<string, NoteTypeColors> = {
  consultation: { backgroundColor: "#EAF4F8", textColor: "#2D647A" },
  "phone call": { backgroundColor: "#F8F3E8", textColor: "#8A6A2D" },
  "follow up": { backgroundColor: "#ECF6F2", textColor: "#2F6F5A" },
  markings: { backgroundColor: "#FCEFF1", textColor: "#A44C63" },
};

const normalizeNoteType = (noteType: string): string =>
  noteType.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

const getStringHash = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const getNoteTypeColors = (noteType: string): NoteTypeColors => {
  const normalized = normalizeNoteType(noteType || "voice note");
  const builtinColor = BUILTIN_NOTE_TYPE_COLORS[normalized];

  if (builtinColor) {
    return builtinColor;
  }

  const paletteIndex =
    getStringHash(normalized) % CUSTOM_NOTE_TYPE_PALETTE.length;
  return CUSTOM_NOTE_TYPE_PALETTE[paletteIndex];
};

const NoteHeader: React.FC<NoteHeaderProps> = ({ note, getNoteTypeLabel }) => {
  const colors = getNoteTypeColors(note.type);

  return (
    <View style={styles.noteHeader}>
      <View style={styles.noteTypeRow}>
        <View
          style={[
            styles.noteTypeContainer,
            { backgroundColor: colors.backgroundColor },
          ]}
        >
          <Text style={[styles.noteType, { color: colors.textColor }]}>
            {getNoteTypeLabel(note.type)}
          </Text>
        </View>
        {note.is_signed && <Text style={styles.signedIndicator}>ᝰ✍🏻</Text>}
      </View>

      <View style={styles.noteDateContainer}>
        <Calendar size={14} color="#666666" />
        <Text style={styles.noteDate}>{formatDate(note.date)}</Text>
        <Clock size={14} color="#666666" style={styles.timeIcon} />
        <Text style={styles.noteTime}>{formatTime(note.date)}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  noteHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  noteTypeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  noteTypeContainer: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  noteType: {
    fontSize: 12,
    fontWeight: "500",
  },
  signedIndicator: {
    fontSize: 16,
  },
  noteDateContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  noteDate: {
    fontSize: 12,
    color: "#666666",
    marginLeft: 4,
  },
  timeIcon: {
    marginLeft: 8,
  },
  noteTime: {
    fontSize: 12,
    color: "#666666",
    marginLeft: 4,
  },
});

export default NoteHeader;
